import { Box, Divider, Grid, Stack, Typography, alpha, useTheme } from '@mui/material';
import { ChildTenant } from '../../../@types/organization';
import UnsavedChangesModal from 'components/util/UnsavedChangesModal';
import { Form, FormikErrors, FormikProvider, FormikState, useFormik } from 'formik';
import * as Yup from 'yup';
import { ReactNode, useEffect, useState } from 'react';
import useIsMountedRef from '../../../hooks/useIsMountedRef';
import { RootState, useSelector } from '../../../redux/store';
import EditItemDialog from 'components/util/EditItemDialog';
import useCustomDispatch, { CustomDispatchErrorResponse } from 'redux/dispatch';
import { useRegisterUnsavedFormChanges } from 'components/form/useRegisterUnsavedFormChanges';
import { DatePicker } from '@mui/x-date-pickers';
import {
  reactivateOrganization,
  updateAiFeedbackEnabled,
  updateSmsEnabled,
  updateTenantDeactivationDate
} from 'redux/slices/organization';
import axios from 'utils/axios';
import { useTranslator } from 'translation/useTranslator';
import { TextField } from '@nps.today/component-library';
import { isAccessible, PermissionClaims, permission } from 'access';
import useAuth from 'hooks/useAuth';
import { useFeatureFlags } from 'contexts/FeatureFlagsContext';
import { FeatureFlagId } from '../../../@types/featureFlags';
import Icon from 'components/icons/Icon';
import { IconType } from '../../../@types/icons';
import CustomSwitch from 'components/util/CustomSwitch';

interface SettingRowProps {
  iconType: IconType;
  tint: string;
  title: ReactNode;
  titleAdornment?: ReactNode;
  description: ReactNode;
  control?: ReactNode;
  children?: ReactNode;
  showDivider: boolean;
}

function SettingRow({
  iconType,
  tint,
  title,
  titleAdornment,
  description,
  control,
  children,
  showDivider
}: Readonly<SettingRowProps>) {
  return (
    <>
      <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ py: 2 }}>
        <Box
          sx={{
            flexShrink: 0,
            width: 36,
            height: 36,
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: tint,
            backgroundColor: alpha(tint, 0.12)
          }}
        >
          <Icon type={iconType} style={{ fontSize: '1.25rem' }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
            <Typography variant="subtitle1" sx={{ fontSize: 15, fontWeight: 500 }}>
              {title}
            </Typography>
            {titleAdornment}
          </Stack>
          {description && (
            <Typography variant="caption" sx={{ fontSize: 13, color: 'text.secondary' }}>
              {description}
            </Typography>
          )}
          {children && <Box sx={{ mt: 1.5 }}>{children}</Box>}
        </Box>
        {control && <Box sx={{ flexShrink: 0, pl: 1 }}>{control}</Box>}
      </Stack>
      {showDivider && <Divider sx={{ borderColor: 'divider' }} />}
    </>
  );
}

interface EditOrganizationDialogProps {
  isOpen: boolean;
  close: () => void;
  organizationIdToEdit?: string;
  onSuccess: () => void;
}

interface InitialState {
  inactiveAt: Date | null;
  active: boolean;
  smsEnabled: boolean;
  isAiFeedbackEnabled: boolean;
  deactivationReminderEmail: string | null;
  afterSubmit?: string;
}

function findOrganizationToEdit(
  organizationIdToEdit: string | undefined,
  childTenants: ChildTenant[],
  currentTenant: ChildTenant | null
): ChildTenant | null {
  if (!organizationIdToEdit) {
    return currentTenant;
  }

  const childTenant = childTenants.find((org) => org.id === organizationIdToEdit);
  if (childTenant) {
    return childTenant;
  }

  return currentTenant?.id === organizationIdToEdit ? currentTenant : null;
}

function getOrganizationChanges(
  organization: ChildTenant,
  values: InitialState,
  canUpdateAiFeedbackEnabled: boolean,
  smsEnabledBaseline: boolean
) {
  const originalInactiveAt = organization.deactivateAt ? new Date(organization.deactivateAt) : null;
  const hasDeactivationDateChanged =
    (originalInactiveAt?.getTime() ?? null) !== (values.inactiveAt?.getTime() ?? null);
  const hasReminderEmailChanged =
    (values.deactivationReminderEmail ?? null) !== (organization.deactivationReminderEmail ?? null);
  const hasSmsEnabledChanged = values.smsEnabled !== smsEnabledBaseline;
  const hasAiFeedbackEnabledChanged =
    canUpdateAiFeedbackEnabled &&
    values.isAiFeedbackEnabled !== (organization.isAiFeedbackEnabled ?? false);

  return {
    hasDeactivationDateChanged,
    hasSmsEnabledChanged,
    hasAiFeedbackEnabledChanged,
    hasToggleChanges: hasSmsEnabledChanged || hasAiFeedbackEnabledChanged,
    shouldReactivate: !organization.active && values.active,
    shouldUpdateDeactivationSettings:
      organization.active && (hasDeactivationDateChanged || hasReminderEmailChanged)
  };
}

function buildTenantPayload(organization: ChildTenant, values: InitialState) {
  return {
    tenantId: organization.id,
    tenantName: organization.name,
    displayName: organization.displayName,
    phoneNumber: organization.phoneNumber,
    invoiceEmail: organization.invoiceEmail,
    vat: organization.vat ?? null,
    website: organization.website,
    parentTenantId: organization.parentTenantId,
    deactivationReminderEmail: values.deactivationReminderEmail ?? null,
    isMfaForced: organization.isMfaForced,
    isPasswordLoginDisabled: organization.isPasswordLoginDisabled,
    isAiFeedbackEnabled: organization.isAiFeedbackEnabled ?? false
  };
}

export default function EditOrganizationDialog(props: Readonly<EditOrganizationDialogProps>) {
  const isMountedRef = useIsMountedRef();
  const customDispatch = useCustomDispatch();
  const [isUnsavedChangesModalOpen, setIsUnsavedChangesModalOpen] = useState(false);
  const [fetchedSmsEnabled, setFetchedSmsEnabled] = useState<boolean | null>(null);
  const translator = useTranslator();
  const theme = useTheme();
  const auth = useAuth();
  const { isEnabledForCurrentOrganization } = useFeatureFlags();

  const canUpdateAiFeedbackEnabled =
    isEnabledForCurrentOrganization(FeatureFlagId.FeedbackAnalysis) &&
    isAccessible([permission(PermissionClaims.ChildTenantUpdate)], auth.user?.claims ?? []);

  const { childTenants, currentTenant } = useSelector((state: RootState) => state.organization);

  const organization = findOrganizationToEdit(
    props.organizationIdToEdit,
    childTenants,
    currentTenant
  );

  const organizationId = organization?.id;
  const organizationSmsEnabled = organization?.smsEnabled ?? false;

  useEffect(() => {
    if (props.isOpen && organizationId) {
      setFetchedSmsEnabled(null);
      axios
        .get<boolean>(`/organization/${organizationId}/sms`)
        .then((response) => {
          setFetchedSmsEnabled(response.data);
        })
        .catch(() => {
          setFetchedSmsEnabled(organizationSmsEnabled);
        });
    }
  }, [props.isOpen, organizationId, organizationSmsEnabled]);

  const schema = Yup.object().shape({
    deactivationReminderEmail: Yup.string()
      .nullable()
      .email(translator.emailInvalid())
      .when('inactiveAt', {
        is: (inactiveAt: Date | null) => organization?.active === true && Boolean(inactiveAt),
        then: Yup.string()
          .nullable()
          .email(translator.emailInvalid())
          .required(translator.deactivationReminderEmailRequired())
      })
  });

  const formik = useFormik<InitialState>({
    enableReinitialize: true,
    validationSchema: schema,
    initialValues: {
      inactiveAt: organization?.deactivateAt ? new Date(organization.deactivateAt) : null,
      active: organization?.active ?? true,
      smsEnabled: fetchedSmsEnabled ?? organization?.smsEnabled ?? false,
      isAiFeedbackEnabled: organization?.isAiFeedbackEnabled ?? false,
      deactivationReminderEmail: organization?.deactivationReminderEmail ?? null
    },
    onSubmit: async (values, { setErrors, setSubmitting, resetForm }) => {
      if (!organization) {
        if (isMountedRef.current) {
          setSubmitting(false);
        }
        return;
      }

      const changes = getOrganizationChanges(
        organization,
        values,
        canUpdateAiFeedbackEnabled,
        fetchedSmsEnabled ?? organization.smsEnabled ?? false
      );
      const tenantPayload = buildTenantPayload(organization, values);

      try {
        if (changes.shouldReactivate) {
          await customDispatch({
            action: reactivateOrganization,
            actionParameters: tenantPayload,
            disableSuccessMessage: changes.hasToggleChanges
          });
        } else if (changes.shouldUpdateDeactivationSettings) {
          await customDispatch({
            action: updateTenantDeactivationDate,
            actionParameters: {
              ...tenantPayload,
              active: values.active,
              deactivateAt: values.inactiveAt
            },
            successMessage: changes.hasDeactivationDateChanged
              ? undefined
              : translator.organizationProfileUpdated(),
            disableSuccessMessage: changes.hasToggleChanges
          });
        }

        if (changes.hasSmsEnabledChanged) {
          await customDispatch({
            action: updateSmsEnabled,
            actionParameters: {
              organizationId: organization.id,
              smsEnabled: values.smsEnabled
            },
            disableSuccessMessage: changes.hasAiFeedbackEnabledChanged
          });
        }

        if (changes.hasAiFeedbackEnabledChanged) {
          await customDispatch({
            action: updateAiFeedbackEnabled,
            actionParameters: {
              ...tenantPayload,
              active: values.active,
              deactivateAt: values.inactiveAt?.toISOString() ?? null,
              isAiFeedbackEnabled: values.isAiFeedbackEnabled
            }
          });
        }

        handleOnSuccess(resetForm, setSubmitting);
      } catch (error: any) {
        handleOnError(error, setErrors, setSubmitting);
      }
    }
  });

  const {
    submitForm,
    resetForm,
    dirty,
    isSubmitting,
    handleSubmit,
    getFieldProps,
    setFieldValue,
    setFieldTouched,
    errors,
    touched
  } = formik;

  useRegisterUnsavedFormChanges(dirty);

  const showSms = Boolean(organization);
  const showAiFeedback = Boolean(organization && canUpdateAiFeedbackEnabled);

  return (
    <>
      <FormikProvider value={formik}>
        <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
          <EditItemDialog
            itemType="organization"
            editable
            isOpen={props.isOpen}
            title={translator.editOrganization()}
            editContent={[
              <Box sx={{ width: '100%' }} key={'edit-tenant-box'}>
                {organization?.active === true && (
                  <SettingRow
                    iconType={IconType.calendar}
                    tint={theme.palette.warning.main}
                    title={translator.deactivation()}
                    description={translator.deactivationDateHelperText(organization.name)}
                    showDivider={showSms || showAiFeedback}
                  >
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <DatePicker
                          label={translator.deactivationDate()}
                          value={getFieldProps('inactiveAt').value}
                          onChange={(value) => {
                            value?.setHours(0, 0, 0, 0);
                            setFieldValue('inactiveAt', value);
                          }}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              size: 'small'
                            },
                            actionBar: {
                              actions: ['clear']
                            }
                          }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                          dataTestId="deactivation-reminder-email"
                          type="email"
                          fullWidth
                          size="small"
                          label={translator.deactivationReminderEmail()}
                          value={getFieldProps('deactivationReminderEmail').value ?? ''}
                          onChange={(e) =>
                            setFieldValue('deactivationReminderEmail', e.target.value || null)
                          }
                          onBlur={() => setFieldTouched('deactivationReminderEmail')}
                          error={Boolean(
                            touched.deactivationReminderEmail && errors.deactivationReminderEmail
                          )}
                          helperText={
                            touched.deactivationReminderEmail && errors.deactivationReminderEmail
                          }
                          placeholder={translator.deactivationReminderEmail()}
                        />
                      </Grid>
                    </Grid>
                  </SettingRow>
                )}
                {organization && organization.active !== true && (
                  <SettingRow
                    iconType={IconType.enable}
                    tint={theme.palette.success.main}
                    title={translator.active()}
                    description={translator.activeHelperText(organization.name)}
                    showDivider={showSms || showAiFeedback}
                    control={
                      <CustomSwitch
                        switchProps={{
                          checked: getFieldProps('active').value ?? false,
                          onChange: (_, selected) => setFieldValue('active', selected),
                          size: 'small'
                        }}
                      />
                    }
                  />
                )}
                {showSms && (
                  <SettingRow
                    iconType={IconType.sms}
                    tint={theme.palette.info.main}
                    title={translator.smsEnabled()}
                    description={translator.smsEnabledHelperText()}
                    showDivider={showAiFeedback}
                    control={
                      <CustomSwitch
                        switchProps={{
                          checked: getFieldProps('smsEnabled').value ?? false,
                          onChange: (_, selected) => setFieldValue('smsEnabled', selected),
                          size: 'small'
                        }}
                      />
                    }
                  />
                )}
                {showAiFeedback && (
                  <SettingRow
                    iconType={IconType.ai}
                    tint={theme.palette.chart.violet[0]}
                    title={translator.aiFeedbackEnabled()}
                    description={translator.aiFeedbackEnabledHelperText()}
                    showDivider={false}
                    control={
                      <CustomSwitch
                        switchProps={{
                          checked: getFieldProps('isAiFeedbackEnabled').value ?? false,
                          onChange: (_, selected) => setFieldValue('isAiFeedbackEnabled', selected),
                          size: 'small',
                          inputProps: { 'data-testid': 'ai-feedback-enabled' } as any
                        }}
                      />
                    }
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
                      <Box sx={{ color: 'info.main', display: 'flex', flexShrink: 0, mt: '2px' }}>
                        <Icon type={IconType.information} style={{ fontSize: '1rem' }} />
                      </Box>
                      <Typography variant="caption" sx={{ fontSize: 13, color: 'text.secondary' }}>
                        {translator.aiFeedbackPermissionNote()}
                      </Typography>
                    </Box>
                  </SettingRow>
                )}
              </Box>
            ]}
            dirty={dirty}
            isSubmitting={isSubmitting}
            close={() => props.close()}
            resetForm={resetForm}
            submitForm={submitForm}
          />
        </Form>
      </FormikProvider>
      <UnsavedChangesModal
        isOpen={isUnsavedChangesModalOpen}
        onCancel={() => {
          setIsUnsavedChangesModalOpen(false);
        }}
        onDisregard={() => {
          resetForm();
          setIsUnsavedChangesModalOpen(false);
          props.close();
        }}
        onSave={() => {
          setIsUnsavedChangesModalOpen(false);
          submitForm();
        }}
      />
    </>
  );

  function handleOnError(
    error: any,
    setErrors: (errors: FormikErrors<InitialState>) => void,
    setSubmitting: (isSubmitting: boolean) => void
  ) {
    const responseError = error as CustomDispatchErrorResponse;
    if (isMountedRef.current) {
      setErrors({ afterSubmit: responseError.error.code });
      setSubmitting(false);
    }
  }

  function handleOnSuccess(
    resetForm: (nextState?: Partial<FormikState<InitialState>> | undefined) => void,
    setSubmitting: (isSubmitting: boolean) => void
  ) {
    props.close();
    resetForm();

    if (isMountedRef.current) {
      setSubmitting(false);
    }

    props.onSuccess();
  }
}
